import { useState, useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Typography,
  Grid,
  Tabs,
  Tab,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import CloseIcon from '@mui/icons-material/Close'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import StarIcon from '@mui/icons-material/Star'
import videoData from '../data/videos.json'

function Education() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))

  const [selectedCategory, setSelectedCategory] = useState('all')
  const [activeVideo, setActiveVideo] = useState(null)

  const { videos, categories } = videoData

  // Filter videos by category
  const filteredVideos = useMemo(() => {
    if (selectedCategory === 'all') {
      return videos
    }
    return videos.filter(video => video.category === selectedCategory)
  }, [videos, selectedCategory])

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue)
    setActiveVideo(null) // Close any open video when changing categories
  }

  const handleVideoClick = (video) => {
    if (activeVideo?.id === video.id) {
      setActiveVideo(null) // Toggle off if clicking same video
    } else {
      setActiveVideo(video)
    }
  }

  const handleCloseVideo = () => {
    setActiveVideo(null)
  }

  const getCategoryColor = (category) => {
    switch (category) {
      case 'technical':
        return 'primary'
      case 'concepts':
        return 'info'
      case 'business':
        return 'success'
      case 'about':
        return 'secondary'
      default:
        return 'default'
    }
  }

  const getCategoryLabel = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId)
    return cat ? cat.label : categoryId
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
        Education
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Learn about PermaFrost NMR technology, testing methodology, and efficiency concepts through our video library.
        <Chip label={`${videos.length} videos`} size="small" sx={{ ml: 1, height: 22 }} />
      </Typography>

      {/* Category Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={selectedCategory}
          onChange={handleCategoryChange}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          aria-label="Video category filter"
        >
          {categories.map((category) => (
            <Tab
              key={category.id}
              label={category.label}
              value={category.id}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Active Video Player */}
      {activeVideo && (
        <Card sx={{ mb: 3, position: 'relative' }}>
          <IconButton
            onClick={handleCloseVideo}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              zIndex: 10,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.8)'
              }
            }}
            aria-label="Close video"
          >
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              position: 'relative',
              paddingBottom: '56.25%', // 16:9 aspect ratio
              height: 0,
              overflow: 'hidden'
            }}
          >
            <iframe
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              src={`https://www.youtube.com/embed/${activeVideo.id}?autoplay=1`}
              title={activeVideo.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </Box>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {activeVideo.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {activeVideo.description}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Video Grid */}
      <Grid container spacing={3}>
        {filteredVideos.map((video) => (
          <Grid item xs={12} sm={6} md={4} key={video.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                border: activeVideo?.id === video.id ? `2px solid ${theme.palette.primary.main}` : 'none',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[8]
                }
              }}
            >
              <CardActionArea onClick={() => handleVideoClick(video)}>
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height={isMobile ? 160 : 180}
                    image={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
                    alt={video.title}
                    sx={{ objectFit: 'cover' }}
                  />
                  {/* Play overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0,0,0,0.3)',
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.9)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <PlayArrowIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                    </Box>
                  </Box>
                  {/* Duration badge */}
                  <Chip
                    icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                    label={video.duration}
                    size="small"
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      bgcolor: 'rgba(0,0,0,0.75)',
                      color: 'white',
                      '& .MuiChip-icon': {
                        color: 'white'
                      }
                    }}
                  />
                  {/* "Start Here" badge for first 3 Data Series videos */}
                  {video.title?.startsWith('Data Series') && (
                    <Chip
                      icon={<StarIcon sx={{ fontSize: 14 }} />}
                      label="Start Here"
                      size="small"
                      color="warning"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }}
                    />
                  )}
                </Box>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.3
                    }}
                  >
                    {video.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      mb: 1.5
                    }}
                  >
                    {video.description}
                  </Typography>
                  <Chip
                    label={getCategoryLabel(video.category)}
                    size="small"
                    color={getCategoryColor(video.category)}
                    variant="outlined"
                  />
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Empty state */}
      {filteredVideos.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No videos found in this category.
          </Typography>
        </Box>
      )}

      {/* Footer note */}
      <Box sx={{ mt: 4, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          These foundational videos explain the methodology and science behind PermaFrost NMR treatment.
          The Data Series videos (#1-3) provide detailed insight into how efficiency improvements are measured and validated.
        </Typography>
      </Box>
    </Box>
  )
}

export default Education
